import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Search, Bell, Settings, ChevronDown, History,
  HelpCircle, X, CheckCircle,
  User, LogOut, RefreshCw, Copy, CheckCheck, MessageSquare,
  Moon, Sun,
} from 'lucide-react';
import { apiClient } from '../../services/api/client';
import { useAuth } from '../../contexts/AuthContext';
import { useSecurityEvents, SecurityEvent } from '../../hooks/useSecurityEvents';
import { useTheme } from '../../App';
import { useUserRole, UserRole } from '../../contexts/UserRoleContext';
import { useToast } from './ToastContainer';
import { fmtDate, fmtDateTime, fmtTime } from '../../utils/date';

interface TopNavProps {
  currentPath: string;
  onNavigate: (path: string) => void;
}

const DEMO_NOTIFS: { id: string; sev: string; msg: string; t: string }[] = [];

const SEV_DOT: Record<string, string> = {
  critical: 'bg-wiz-red',
  warning:  'bg-wiz-yellow',
  info:     'bg-wiz-blue',
};

// ── Notification types ──────────────────────────────────────────────────────

interface WsNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  timestamp: string;
  read: boolean;
}

// Live critical/high notifications now route through the single app-wide
// ToastContainer (useToast) instead of a second fixed bottom-right stack that
// overlapped it. The bell dropdown remains the persistent, navigable list.

// ── Global search ────────────────────────────────────────────────────────────
//
// This used to be a SECOND full search implementation: its own debounce, its
// own /api/v1/data-plane/search call, its own result mapping and dropdown —
// all duplicating CommandPalette, which queries the same endpoint with the
// same limit and maps the same five kinds. Two implementations meant two
// different result lists and two different destinations for one query
// (`/issue/:id` here, `?focus=` there), depending on whether the user clicked
// the box or pressed Cmd-K. The box stays — it is where a Wiz user looks —
// but it now opens the one palette. `/` does the same.

// How long to gather live issue events before raising ONE summary toast.
const BURST_WINDOW_MS = 6000;

const openCommandPalette = () => {
  window.dispatchEvent(new CustomEvent('open-command-palette'));
};

const GlobalSearch: React.FC<{ onNavigate: (p: string) => void }> = () => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = document.activeElement?.tagName;
      if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA') {
        e.preventDefault();
        openCommandPalette();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return (
    <button
      type="button"
      onClick={openCommandPalette}
      className="relative w-72 flex items-center gap-2 pl-9 pr-3 py-1.5 bg-wiz-card border border-wiz-border rounded-lg text-sm text-gray-500 hover:text-gray-300 hover:border-wiz-purple/40 transition-colors text-left"
      aria-label="Search resources, issues, identities and attack paths (Ctrl+K or /)"
    >
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600" aria-hidden="true" />
      <span className="flex-1 truncate">Search</span>
      <kbd className="text-2xs text-gray-600 border border-wiz-border rounded px-1 py-0.5">⌘K</kbd>
    </button>
  );
};

// ── IconBtn ──────────────────────────────────────────────────────────────────

const IconBtn: React.FC<{ title: string; onClick?: () => void; children: React.ReactNode; badge?: number; ariaExpanded?: boolean; ariaHasPopup?: boolean | 'menu' | 'listbox' | 'dialog' }> = ({ title, onClick, children, badge, ariaExpanded, ariaHasPopup }) => (
  <button
    title={title}
    aria-label={badge !== undefined && badge > 0 ? `${title}, ${badge} new` : title}
    onClick={onClick}
    aria-expanded={ariaExpanded}
    aria-haspopup={ariaHasPopup}
    className="relative p-1.5 text-gray-500 hover:text-gray-200 rounded-md hover:bg-wiz-card transition-colors duration-150"
  >
    <span aria-hidden="true">{children}</span>
    {badge !== undefined && badge > 0 && (
      <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 bg-wiz-red text-white text-xs font-bold rounded-full flex items-center justify-center notification-dot animate-pulse" aria-hidden="true">
        {badge > 99 ? '99+' : badge}
      </span>
    )}
  </button>
);

// ── Tenant switcher ─────────────────────────────────────────────────────────

interface Tenant {
  id: string;
  name: string;
  icon: string;
}

// Fallback seed until the real tenants load from /api/v1/admin/tenants.
const TENANTS: Tenant[] = [
  { id: 'default', name: 'Default Tenant', icon: 'D' },
];

// Deterministic color per tenant id so any real tenant gets a stable swatch.
const _SWATCH = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-purple-500', 'bg-pink-500', 'bg-cyan-500'];
const _RING = [
  'border-blue-600/25 bg-blue-600/15 text-blue-400',
  'border-emerald-600/25 bg-emerald-600/15 text-emerald-400',
  'border-amber-600/25 bg-amber-600/15 text-amber-400',
  'border-purple-600/25 bg-purple-600/15 text-purple-400',
  'border-pink-600/25 bg-pink-600/15 text-pink-400',
  'border-cyan-600/25 bg-cyan-600/15 text-cyan-400',
];
function _hash(s: string): number { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h; }
function tenantColor(id: string): string { return _SWATCH[_hash(id) % _SWATCH.length]; }
function tenantRing(id: string): string { return _RING[_hash(id) % _RING.length]; }

function getStoredTenant(): Tenant {
  try {
    const stored = localStorage.getItem('current_tenant');
    if (stored) {
      const parsed = JSON.parse(stored) as Tenant;
      if (parsed && parsed.id) return parsed;
    }
  } catch { /* ignore */ }
  return TENANTS[0];
}

const TenantSwitcher: React.FC<{ onNavigate: (p: string) => void }> = ({ onNavigate }) => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<Tenant>(getStoredTenant);
  const [tenants, setTenants] = useState<Tenant[]>([getStoredTenant()]);
  const ref = useRef<HTMLDivElement>(null);

  // Load the REAL tenants (was a hardcoded Demo Acme / Acme Corp / TechStart list).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // apiClient, not raw fetch: this sent X-Tenant-ID but no Authorization
        // header. /api/v1/admin/tenants answers 200 unauthenticated on this
        // deployment, so the switcher populates today — but a 401 anywhere it
        // IS gated is swallowed by `if (!res.ok) return` and the switcher
        // silently falls back to showing only the seed tenant.
        const { data: j, error } = await apiClient.get<any>('/api/v1/admin/tenants');
        if (error) return;
        const list: Tenant[] = (j?.tenants ?? j?.items ?? j ?? [])
          .filter((t: any) => t && (t.id || t.slug))
          .map((t: any) => ({ id: t.id || t.slug, name: t.name || t.id, icon: (t.name || t.id || '?').charAt(0).toUpperCase() }));
        if (alive && list.length) {
          setTenants(list);
          const match = list.find(t => t.id === current.id);
          if (match && match.name !== current.name) { setCurrent(match); localStorage.setItem('current_tenant', JSON.stringify(match)); }
        }
      } catch { /* offline — keep the seed */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const switchTenant = (tenant: Tenant) => {
    setCurrent(tenant);
    localStorage.setItem('current_tenant', JSON.stringify(tenant));
    localStorage.setItem('tenant_id', tenant.id);
    setOpen(false);
    // Reload so all data refetches with new tenant header
    window.location.reload();
  };

  const ringClass = tenantRing(current.id);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 border text-xs font-medium px-2 py-0.5 rounded-full
          hover:brightness-125 transition-all ${ringClass}`}
        aria-label={`Current tenant: ${current.name}. Click to switch tenants.`}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <div className={`w-4 h-4 rounded-sm ${tenantColor(current.id)} flex items-center justify-center shrink-0`}>
          <span className="text-white text-xs font-bold leading-none">{current.icon}</span>
        </div>
        {current.name}
        <ChevronDown className={`w-3 h-3 opacity-70 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-1 w-56 bg-wiz-dark border border-wiz-border
            rounded-xl shadow-2xl z-50 py-1 overflow-hidden"
          role="listbox"
          aria-label="Select tenant"
        >
          <p className="px-3 pt-2 pb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-600">
            Switch Tenant
          </p>
          {tenants.map(tenant => {
            const isActive = tenant.id === current.id;
            return (
              <button
                key={tenant.id}
                role="option"
                aria-selected={isActive}
                onClick={() => switchTenant(tenant)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors
                  ${isActive
                    ? 'bg-wiz-card text-white'
                    : 'text-gray-400 hover:text-white hover:bg-wiz-card'
                  }`}
              >
                <div className={`w-6 h-6 rounded-md ${tenantColor(tenant.id)} flex items-center justify-center shrink-0`}>
                  <span className="text-white text-xs font-bold leading-none">{tenant.icon}</span>
                </div>
                <div className="flex-1 text-left">
                  <span className="block text-sm">{tenant.name}</span>
                  <span className="block text-xs text-gray-600">{tenant.id}</span>
                </div>
                {isActive && (
                  <CheckCircle className="w-3.5 h-3.5 text-green-400 shrink-0" />
                )}
              </button>
            );
          })}
          <div className="border-t border-wiz-border mt-1 pt-1">
            <button
              onClick={() => { onNavigate('/settings'); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-500 hover:text-gray-300 hover:bg-wiz-card transition-colors"
            >
              <Settings className="w-3.5 h-3.5" />
              Manage Tenants
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── useWsNotifications — shared live stream with polling fallback ────────────
//
// R133: this used to open its OWN WebSocket to /ws/events, built from
// `API_CONFIG.BASE_URL` with **no token**. The gateway authenticates that
// endpoint (?token=<jwt>), so the socket could only ever be rejected with 403 —
// the bell's "Live" dot was therefore permanently off, notifications came only
// from the 30s poll, and a dead socket was retried every 10 seconds forever.
//
// TopNav already consumes the canonical, authenticated, shared singleton via
// useSecurityEvents(). Per the parity mandate we keep that one and drop the
// duplicate: notifications and the connected state now derive from the shared
// stream, and polling remains purely as the degraded-mode fallback.

function useWsNotifications() {
  const [notifications, setNotifications] = useState<WsNotification[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const { events: liveEvents, isConnected: wsConnected } = useSecurityEvents();

  const addNotification = useCallback((n: WsNotification) => {
    setNotifications(prev => {
      const next = [n, ...prev.filter(x => x.id !== n.id)];
      if (next.length > 50) next.length = 50;
      return next;
    });
  }, []);

  const markRead = useCallback((id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  // Start polling fallback
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    const poll = async () => {
      const { data, error: err } = await apiClient.get<any>('/api/v1/notifications/recent');
      if (err || !data) return;
      const items: any[] = Array.isArray(data) ? data : (data?.notifications ?? data?.items ?? []);
      if (mountedRef.current && items.length > 0) {
        setNotifications(prev => {
          const existingIds = new Set(prev.map(n => n.id));
          const newItems: WsNotification[] = items
            .filter((n: any) => !existingIds.has(n.id))
            .map((n: any) => ({
              id: n.id || `poll-${Date.now()}-${Math.random().toString(36).slice(2)}`,
              type: n.type || 'info',
              title: n.title || 'Notification',
              message: n.message || n.description || '',
              severity: n.severity || 'info',
              timestamp: n.timestamp || n.created_at || new Date().toISOString(),
              read: n.read ?? false,
            }));
          const merged = [...newItems, ...prev];
          if (merged.length > 50) merged.length = 50;
          return merged;
        });
      }
    };
    poll(); // Initial fetch
    pollRef.current = setInterval(poll, 30000);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Fold events from the shared authenticated stream into the bell. The
  // singleton buffers newest-first, so walking a short prefix is enough;
  // addNotification dedupes by id.
  useEffect(() => {
    if (!mountedRef.current || liveEvents.length === 0) return;
    for (let i = Math.min(liveEvents.length, 10) - 1; i >= 0; i--) {
      const ev = liveEvents[i];
      if (!ev) continue;
      const raw = (ev.raw || {}) as Record<string, unknown>;
      const sev = ev.severity as WsNotification['severity'] | undefined;
      addNotification({
        id: ev.id,
        type: ev.type || 'info',
        title: ev.title || 'Notification',
        message: (raw.message as string) || (raw.description as string) || '',
        severity: sev && ['critical', 'high', 'medium', 'low', 'info'].includes(sev) ? sev : 'info',
        timestamp: ev.ts || new Date().toISOString(),
        read: false,
      });
    }
  }, [liveEvents, addNotification]);

  // Poll only while the live stream is down (degraded mode). When the shared
  // socket connects, polling stops; if it drops, polling resumes.
  useEffect(() => {
    mountedRef.current = true;
    if (wsConnected) stopPolling();
    else startPolling();
    return () => { stopPolling(); };
  }, [wsConnected, startPolling, stopPolling]);

  useEffect(() => () => { mountedRef.current = false; }, []);

  return { notifications, wsConnected, markRead, markAllRead, addNotification };
}

// ── TopNav ───────────────────────────────────────────────────────────────────

const ROLE_BADGE: Record<UserRole, { label: string; cls: string }> = {
  admin:  { label: 'Admin',  cls: 'bg-purple-500/20 text-purple-400' },
  editor: { label: 'Editor', cls: 'bg-blue-500/20 text-blue-400' },
  viewer: { label: 'Viewer', cls: 'bg-gray-500/20 text-gray-400' },
};

const TopNav: React.FC<TopNavProps> = ({ currentPath, onNavigate }) => {
  const { user, isAuthenticated, logout, getAuthToken } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const { role } = useUserRole();
  const [notifOpen,  setNotifOpen]  = useState(false);
  const [authOpen,   setAuthOpen]   = useState(false);
  const [copied,     setCopied]     = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const authRef  = useRef<HTMLDivElement>(null);

  // Real-time events for notification bell (existing security events hook)
  const { events: liveEvents } = useSecurityEvents();
  const [seenCount, setSeenCount] = useState(0);
  const liveNotifs = liveEvents.filter(
    (e: SecurityEvent) => e.severity === 'critical' || e.severity === 'high' || e.type === 'toxic_combination.detected'
  );

  // WebSocket notifications
  const { notifications: wsNotifs, wsConnected, markRead, markAllRead } = useWsNotifications();

  // Live critical/high notifications route through the single app-wide
  // ToastContainer (auto-dismiss + capped + deduped) — no second stack here.
  const { addToast } = useToast();
  const seenToastIds = useRef<Set<string>>(new Set());
  // Burst buffer for live issue toasts — see the coalescing block below.
  const toastBurst = useRef<{ title: string; critical: boolean }[]>([]);
  const burstTimer = useRef<number | null>(null);

  // Show toast when a new critical/high WS notification arrives
  useEffect(() => {
    if (wsNotifs.length === 0) return;
    const latest = wsNotifs[0];
    if (!latest || seenToastIds.current.has(latest.id)) return;
    seenToastIds.current.add(latest.id);
    if (latest.severity === 'critical' || latest.severity === 'high') {
      addToast(latest.title, latest.severity === 'critical' ? 'error' : 'warning', 6000);
    }
  }, [wsNotifs, addToast]);

  // R193-SHIP-AM: also raise a toast for P0/P1 issue lifecycle events from
  // useSecurityEvents (gateway /ws/events) — issue.created / issue.reopened /
  // issue.escalated / sla.breached.
  useEffect(() => {
    if (liveEvents.length === 0) return;
    // Only check the newest events; the buffer pushes at index 0.
    for (let i = 0; i < Math.min(liveEvents.length, 5); i++) {
      const ev = liveEvents[i];
      if (!ev || seenToastIds.current.has(ev.id)) continue;
      const isLifecycle =
        ev.type === 'issue.created' ||
        (ev.type as string) === 'issue.reopened' ||
        (ev.type as string) === 'issue.escalated' ||
        (ev.type as string) === 'issue.escalation_fired' ||
        // R193-SHIP-AV: sla.breached always raises a toast regardless of
        // priority/severity (operators must see SLA breaches immediately).
        (ev.type as string) === 'sla.breached';
      if (!isLifecycle) continue;
      const raw = (ev.raw || {}) as Record<string, unknown>;
      const issue = (raw.issue as Record<string, unknown>) || raw;
      const priority = (issue.priority as string | undefined) || (raw.priority as string | undefined);
      const severity = (issue.severity as string | undefined) || (raw.severity as string | undefined);
      // R193-SHIP-AV: sla.breached bypasses the P0/P1 + critical/high gate.
      const isSlaBreach = (ev.type as string) === 'sla.breached';
      const isHigh =
        isSlaBreach
        || priority === 'P0' || priority === 'P1'
        || severity === 'critical' || severity === 'high';
      if (!isHigh) continue;
      seenToastIds.current.add(ev.id);
      const isCritical = isSlaBreach || priority === 'P0' || severity === 'critical';
      // COALESCE. One toast per qualifying event turned a running scan into a
      // wall of five stacked cards over the bottom-right of every page, each
      // repeating a variant of the same sentence. Buffer the burst and raise a
      // single summary toast, the way Wiz shows one "N new issues" pill.
      toastBurst.current.push({ title: ev.title, critical: isCritical });
      if (burstTimer.current === null) {
        burstTimer.current = window.setTimeout(() => {
          const burst = toastBurst.current;
          toastBurst.current = [];
          burstTimer.current = null;
          if (burst.length === 0) return;
          const anyCritical = burst.some(b => b.critical);
          if (burst.length === 1) {
            addToast(burst[0].title, anyCritical ? 'error' : 'warning', 6000);
          } else {
            addToast(
              `${burst.length} new ${anyCritical ? 'critical/high' : 'high'} issues`,
              anyCritical ? 'error' : 'warning',
              8000,
              { label: 'View', onClick: () => onNavigate('/issues') },
            );
          }
        }, BURST_WINDOW_MS);
      }
    }
  }, [liveEvents, addToast, onNavigate]);

  // Combine live events + WS notifications for the dropdown
  const allNotifs: WsNotification[] = [
    ...wsNotifs,
    ...liveNotifs
      .filter(e => !wsNotifs.some(wn => wn.id === e.id))
      .map(e => ({
        id: e.id,
        type: e.type,
        title: e.title,
        message: '',
        severity: (e.severity || 'info') as WsNotification['severity'],
        timestamp: e.ts,
        read: false,
      })),
  ].slice(0, 50);

  const unreadCount = allNotifs.filter(n => !n.read).length;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
      if (authRef.current  && !authRef.current.contains(e.target as Node))  setAuthOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const userInitials = user
    ? (user.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || user.email.slice(0, 2).toUpperCase())
    : '??';

  const copyToken = () => {
    const token = getAuthToken();
    if (token) {
      navigator.clipboard.writeText(token).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const truncateToken = (token: string | null) => {
    if (!token) return 'No token';
    return `${token.slice(0, 20)}\u2026${token.slice(-8)}`;
  };

  const handleNotifOpen = () => {
    setNotifOpen(o => {
      if (!o) {
        markAllRead();
        setSeenCount(liveNotifs.length);
      }
      return !o;
    });
  };

  const handleNotifClick = (n: WsNotification) => {
    markRead(n.id);
  };

  const sevDotColor = (sev: string) => {
    switch (sev) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-blue-400';
      default: return 'bg-gray-500';
    }
  };

  return (
    <>
      <header className="h-11 shrink-0 flex items-center gap-2 px-3 bg-wiz-dark border-b border-wiz-border z-30" role="banner" aria-label="Top navigation bar">

        {/* -- Left -- */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Logo */}
          <div className="flex items-center gap-2 mr-1">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-wiz-purple to-wiz-blue flex items-center justify-center shrink-0">
              <span className="text-white font-black text-[13px] leading-none tracking-tight">C</span>
            </div>
            <span className="text-white font-semibold text-sm tracking-tight hidden md:block">CyberShield</span>
          </div>

          {/* Tenant Switcher */}
          <TenantSwitcher onNavigate={onNavigate} />
          {/* Projects + View removed (2026-06-18): the Projects dropdown was a
              non-functional duplicate of the sidebar's R82 Project Scope Switcher,
              and View was a dead placeholder DropBtn with no handler. Wiz keeps the
              top bar lean — project scoping lives in the sidebar. */}
        </div>

        {/* -- Center -- */}
        <div className="flex-1 flex justify-center">
          <GlobalSearch onNavigate={onNavigate} />
        </div>

        {/* -- Right -- */}
        <div className="flex items-center gap-0.5 shrink-0">
          {/* R128 — Live scan progress indicator (subscribes to scan.progress events) */}
          <ScanProgressIndicator events={liveEvents} />

          {/* Role badge */}
          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${ROLE_BADGE[role].cls} mr-1`}>
            {ROLE_BADGE[role].label}
          </span>

          <IconBtn title="Recent activity" onClick={() => onNavigate('/audit-logs')}>
            <History className="w-4 h-4" />
          </IconBtn>

          {/* Notifications */}
          <div ref={notifRef} className="relative">
            <IconBtn title="Notifications" onClick={handleNotifOpen} badge={unreadCount} ariaExpanded={notifOpen} ariaHasPopup="dialog">
              <Bell className="w-4 h-4" />
            </IconBtn>
            {notifOpen && (
              <div className="absolute top-full right-0 mt-1 w-80 bg-wiz-dark border border-wiz-border
                rounded-xl shadow-2xl z-50 overflow-hidden"
                role="dialog" aria-label="Notifications panel" aria-live="polite">
                <div className="flex items-center justify-between px-4 py-3 border-b border-wiz-border">
                  <div className="flex items-center gap-2">
                    <h2 className="text-white font-semibold text-sm">Notifications</h2>
                    {wsConnected && (
                      <span className="flex items-center gap-1 text-xs text-green-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Live
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {allNotifs.some(n => !n.read) && (
                      <button onClick={markAllRead} className="text-xs text-wiz-purple hover:underline">
                        Mark all read
                      </button>
                    )}
                    <button onClick={() => setNotifOpen(false)} aria-label="Close notifications" className="text-gray-500 hover:text-gray-300 transition-colors">
                      <X className="w-4 h-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div className="max-h-80 overflow-y-auto divide-y divide-wiz-border">
                  {allNotifs.length === 0 ? (
                    <div className="px-4 py-6 text-center text-gray-500 text-xs">
                      No recent notifications
                    </div>
                  ) : allNotifs.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => handleNotifClick(n)}
                      className={`w-full text-left px-4 py-3 hover:bg-wiz-card transition-colors ${n.read ? 'opacity-60' : ''}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${sevDotColor(n.severity)}`} />
                        <div className="min-w-0 flex-1">
                          <p className="text-gray-200 text-xs leading-relaxed truncate">{n.title}</p>
                          {n.message && (
                            <p className="text-gray-500 text-xs mt-0.5 truncate">{n.message}</p>
                          )}
                          <p className="text-gray-600 text-xs mt-0.5">
                            {n.type.replace(/\./g, ' ')}{n.timestamp ? ` \u00B7 ${fmtTime(n.timestamp)}` : ''}
                          </p>
                        </div>
                        {!n.read && (
                          <div className="w-1.5 h-1.5 rounded-full bg-wiz-purple mt-2 shrink-0" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
                <div className="px-4 py-2.5 border-t border-wiz-border">
                  <button onClick={() => { onNavigate('/notifications'); setNotifOpen(false); }}
                    className="text-wiz-purple text-xs hover:underline transition-colors">
                    View all notifications &rarr;
                  </button>
                </div>
              </div>
            )}
          </div>

          <IconBtn
            title="AI Security Assistant"
            onClick={() => window.dispatchEvent(new CustomEvent('open-ai-assistant'))}
          >
            <MessageSquare className="w-4 h-4 text-wiz-purple" />
          </IconBtn>

          <IconBtn
            title="Keyboard shortcuts (?)"
            onClick={() => window.dispatchEvent(new CustomEvent('open-shortcuts'))}
          >
            <HelpCircle className="w-4 h-4" />
          </IconBtn>

          <IconBtn title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'} onClick={toggleDarkMode}>
            {darkMode ? <Sun className="w-4 h-4 text-yellow-400" /> : <Moon className="w-4 h-4" />}
          </IconBtn>

          <IconBtn title="Settings" onClick={() => onNavigate('/settings')}>
            <Settings className="w-4 h-4" />
          </IconBtn>

          {/* Auth Context Avatar + Dropdown */}
          <div className="relative ml-1" ref={authRef}>
            <button
              onClick={() => setAuthOpen(v => !v)}
              className={`flex items-center gap-1.5 px-1.5 py-0.5 rounded-md transition-all
                ${authOpen ? 'bg-wiz-card ring-1 ring-wiz-purple/50' : 'hover:bg-wiz-card/60'}`}
              title="Auth context"
              aria-label={`User menu for ${user?.full_name || user?.email || 'user'}`}
              aria-expanded={authOpen}
              aria-haspopup="dialog"
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-bold shrink-0
                ${isAuthenticated
                  ? 'bg-gradient-to-br from-wiz-purple to-wiz-blue ring-1 ring-green-500/60'
                  : 'bg-gray-700 ring-1 ring-red-500/60'}`}
              >
                {userInitials}
              </div>
              {user && (
                <div className="hidden lg:flex flex-col items-start leading-tight">
                  <span className="text-gray-200 text-[11px] font-medium">{user.full_name || user.username}</span>
                  <span className="text-gray-500 text-xs">{user.role}</span>
                </div>
              )}
              <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${isAuthenticated ? 'bg-green-500' : 'bg-red-500'}`} />
            </button>

            {/* Auth context dropdown */}
            {authOpen && (
              <div className="absolute right-0 top-full mt-1.5 w-80 bg-wiz-dark border border-wiz-border rounded-lg shadow-2xl z-50 overflow-hidden"
                role="dialog" aria-label="User account menu">
                {/* Header */}
                <div className="px-4 py-3 bg-wiz-card/50 border-b border-wiz-border">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0
                      ${isAuthenticated ? 'bg-gradient-to-br from-wiz-purple to-wiz-blue' : 'bg-gray-700'}`}>
                      {userInitials}
                    </div>
                    <div className="min-w-0">
                      <p className="text-white text-sm font-semibold truncate">{user?.full_name || user?.username || 'Not authenticated'}</p>
                      <p className="text-gray-400 text-xs truncate">{user?.email || '\u2014'}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold
                          ${isAuthenticated ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                          <div className={`w-1 h-1 rounded-full ${isAuthenticated ? 'bg-green-500' : 'bg-red-500'} animate-pulse`} />
                          {isAuthenticated ? 'Authenticated' : 'Not authenticated'}
                        </span>
                        {user?.role && (
                          <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-semibold bg-wiz-purple/20 text-wiz-purple">
                            {user.role}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Token section */}
                <div className="px-4 py-2.5 border-b border-wiz-border">
                  <p className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 font-medium">Auth Token</p>
                  <div className="flex items-center gap-2 bg-wiz-card rounded-md px-2.5 py-1.5">
                    <code className="text-green-400 text-xs font-mono flex-1 truncate">
                      {truncateToken(getAuthToken())}
                    </code>
                    <button onClick={copyToken} aria-label={copied ? 'Token copied' : 'Copy auth token'} className="text-gray-500 hover:text-gray-200 transition-colors shrink-0">
                      {copied ? <CheckCheck className="w-3 h-3 text-green-400" aria-hidden="true" /> : <Copy className="w-3 h-3" aria-hidden="true" />}
                    </button>
                  </div>
                </div>

                {/* Module permissions */}
                {user?.permissions_summary && (
                  <div className="px-4 py-2.5 border-b border-wiz-border">
                    <p className="text-gray-500 text-xs uppercase tracking-wide mb-2 font-medium">Module Permissions</p>
                    <div className="space-y-1">
                      {Object.entries(user.permissions_summary).map(([mod, perms]) => (
                        <div key={mod} className="flex items-center justify-between">
                          <span className="text-gray-400 text-xs uppercase tracking-wide w-12">{mod}</span>
                          <div className="flex items-center gap-1">
                            {Object.entries(perms as Record<string, boolean>).map(([action, allowed]) => (
                              <span key={action}
                                className={`px-1 py-0.5 rounded text-xs font-medium
                                  ${allowed ? 'bg-wiz-purple/20 text-wiz-purple' : 'bg-gray-800 text-gray-600'}`}>
                                {action}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Scope / roles */}
                {user?.modules && !user?.permissions_summary && (
                  <div className="px-4 py-2.5 border-b border-wiz-border">
                    <p className="text-gray-500 text-xs uppercase tracking-wide mb-2 font-medium">Access Scope</p>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(user.modules).flatMap(([mod, actions]) =>
                        (actions as string[]).map(a => (
                          <span key={`${mod}:${a}`} className="px-1.5 py-0.5 rounded text-xs bg-wiz-purple/20 text-wiz-purple font-medium">
                            {mod}:{a}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="px-3 py-2 flex items-center justify-between">
                  <button
                    onClick={() => { onNavigate('/settings'); setAuthOpen(false); }}
                    className="flex items-center gap-1.5 text-gray-400 hover:text-gray-200 text-xs transition-colors px-2 py-1 rounded hover:bg-wiz-card"
                  >
                    <User className="w-3 h-3" /> Profile
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="flex items-center gap-1.5 text-gray-400 hover:text-gray-200 text-xs transition-colors px-2 py-1 rounded hover:bg-wiz-card"
                  >
                    <RefreshCw className="w-3 h-3" /> Refresh auth
                  </button>
                  <button
                    onClick={() => { logout(); setAuthOpen(false); }}
                    className="flex items-center gap-1.5 text-red-400 hover:text-red-300 text-xs transition-colors px-2 py-1 rounded hover:bg-wiz-card"
                  >
                    <LogOut className="w-3 h-3" /> Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

// R128 — Live Scan Progress Indicator
// Subscribes to scan.progress events from the global useSecurityEvents hook,
// shows a pulsing pill in the top bar whenever a scan is mid-flight. Auto-hides
// 6 seconds after the latest progress event so the UI doesn't get cluttered
// once a scan finishes (scan.completed event clears it immediately).
const ScanProgressIndicator: React.FC<{ events: SecurityEvent[] }> = ({ events }) => {
  const [active, setActive] = useState<{ scan_id: string; percent: number; stage?: string; tenant_id?: string } | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Latest scan.progress event wins; scan.completed clears state.
    for (let i = events.length - 1; i >= 0; i--) {
      const e: any = events[i];
      if (e.type === 'scan.completed' || e.type === 'scan.finished') {
        setActive(null);
        if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
        return;
      }
      if (e.type === 'scan.progress' || e.type === 'scan.started') {
        const data = e.data || e.payload || {};
        const scanId = data.scan_id || e.scan_id || 'scan';
        const percent = typeof data.progress_percent === 'number' ? data.progress_percent :
                        typeof data.percent === 'number' ? data.percent :
                        typeof e.percent === 'number' ? e.percent : 0;
        setActive({
          scan_id: scanId,
          percent: Math.max(0, Math.min(100, percent)),
          stage: data.stage || data.step || data.message,
          tenant_id: data.tenant_id,
        });
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(() => setActive(null), 8000);
        return;
      }
    }
  }, [events]);

  if (!active) return null;

  return (
    <div
      className="flex items-center gap-1.5 px-2 py-0.5 mr-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-[11px] text-blue-300"
      title={`Scan ${active.scan_id} — ${active.stage || 'in progress'}`}
    >
      <span className="relative flex w-1.5 h-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-60" />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-400" />
      </span>
      <span className="font-mono">{active.percent}%</span>
      <span className="hidden md:inline text-blue-400/70 truncate max-w-[140px]">
        {active.stage || 'Scanning'}
      </span>
    </div>
  );
};

export default TopNav;
